// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductImagesBadges from "@eisinc/App/Components/CustomBadges/ProductImagesBadges";
import ProductGridContainer, {
    ProductGridContainerStyle,
} from "@eisinc/App/Components/ProductGridCard/ProductGridContainer";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { ProductListProductInformationStyles } from "@eisinc/Overrides/Widgets/ProductList/ProductListProductInformation";
import { sharedProductDescriptionStyles } from "@eisinc/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@insite/content-library/Widgets/ProductList/ProductListActions";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";
// XNG-Change
type Props = ProductCardSelections & HasProduct & { isLoading?: boolean };

export interface ProductListProductGridCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    sponsoredText?: TypographyPresentationProps;
    // XNG-Changes
    productGridContainerStyles?: ProductGridContainerStyle;
    productListImageStyles?: ProductListProductImageStyles;
    productInformationStyles?: ProductListProductInformationStyles;
    badgesContainerStyle?: InjectableCss;
    productMiniCardContainer?: ProductGridContainerStyle;
}

export const productGridCardStyles: ProductListProductGridCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
            padding-bottom: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            margin: auto;
            padding: 10px 50px;
        `,
    },
    sponsoredText: {
        css: css`
            font-size: 0.9em;
            opacity: 0.5;
            position: absolute;
            top: 12px;
        `,
    },
};

const styles = productGridCardStyles;

const ProductListProductGridCard: FC<Props> = ({
    product,
    showImage,
    showCompare,
    showAttributes,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    showPrice,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    // XNG-Change
    isLoading,
}) => {
    // <XNG-Changes>

    const isTabletScreen = useAppSelector(state => (state as CustomAppState)?.xCommon?.isTabletScreen);

    const badges = (product?.properties?.["badges"] && JSON.parse(product.properties["badges"])) || [];

    const customBadges = badges.length > 0 && (
        <StyledWrapper {...styles.badgesContainerStyle} {...{ view: isTabletScreen ? "miniCard" : "grid" }}>
            <ProductImagesBadges product={product} badges={badges} />
        </StyledWrapper>
    );
    // </XNG-Changes>
    // XNG-Change use ProductGridContainer
    return (
        <ProductGridContainer
            extendedStyles={isTabletScreen ? styles.productMiniCardContainer : styles.productGridContainerStyles}
            id={`product_${product?.id ? product.id : ""}`}
            data-test-selector="productContainer"
        >
            <ProductGridContainer.Image>
                <ProductListProductImage
                    showImage={showImage}
                    showCompare={false}
                    showImageBadge={showImageBadges}
                    showTextBadge={showTextBadges}
                    // XNG-Changes
                    extendedStyles={styles.productListImageStyles}
                    {...{ isLoading, view: "Grid" }}
                />

                {!isTabletScreen && customBadges}
            </ProductGridContainer.Image>
            <ProductGridContainer.Info>
                <ProductListProductInformation
                    showAttributes={false}
                    showAvailability={false}
                    showBrand={showBrand}
                    showPartNumbers={showPartNumbers}
                    showTitle={showTitle}
                    showImageBadges={showImageBadges}
                    maxImageBadges={maximumNumberOfImageBadges}
                    showTextBadges={showTextBadges}
                    maxTextBadges={maximumNumberOfTextBadges}
                    {...{
                        extendedStyles: {
                            ...styles.productInformationStyles,
                            descriptionStyles: {
                                ...(isTabletScreen
                                    ? sharedProductDescriptionStyles({ lines: 3 })
                                    : sharedProductDescriptionStyles()),
                            },
                        },
                        isLoading,
                    }}
                />

                {isTabletScreen && customBadges}
            </ProductGridContainer.Info>

            <ProductGridContainer.Availability>
                <ProductListProductInformation
                    showAttributes={false}
                    showAvailability={showAvailability}
                    showBrand={false}
                    showPartNumbers={false}
                    showTitle={false}
                    showImageBadges={false}
                    showTextBadges={false}
                    {...{
                        isLoading,
                    }}
                />
            </ProductGridContainer.Availability>

            <ProductGridContainer.Price>
                <ProductListActions
                    showPrice={showPrice}
                    showAddToList={false}
                    // XNG-Changes
                    {...{ isLoading, showActions: false }}
                />
            </ProductGridContainer.Price>

            <ProductGridContainer.AddToList>
                <ProductListActions
                    showPrice={false}
                    showAddToList={showAddToList}
                    // XNG-Changes
                    {...{ showCompare, isLoading, showActions: false }}
                />
            </ProductGridContainer.AddToList>

            <ProductGridContainer.AddToCartActions>
                <ProductListActions
                    showPrice={false}
                    showAddToList={false}
                    // XNG-Changes
                    {...{ isLoading, showActions: true }}
                />
            </ProductGridContainer.AddToCartActions>
        </ProductGridContainer>
    );
};

export default withProduct(ProductListProductGridCard);
